import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    FileText,
    Download,
    Plus,
    Users,
    Building2,
    TrendingUp,
    DollarSign,
    Calendar,
} from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from '../../i18n/i18n';
import api from '../../api/axios';
import ReusableTable from '../../components/Tableau';
import AddInvoiceModal from '../../components/Modals/AddInvoiceModal';

const InvoicesPage = ({ lang }) => {
    const { t } = useTranslation(lang);
    const navigate = useNavigate();

    // États pour les factures clients
    const [customerInvoices, setCustomerInvoices] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(true);
    const [customerStats, setCustomerStats] = useState({
        total: 0,
        paid: 0,
        unpaid: 0,
        overdue: 0,
        totalAmount: 0,
        paidAmount: 0,
        amountsByCurrency: {}
    });

    // États pour les factures fournisseurs
    const [providerInvoices, setProviderInvoices] = useState([]);
    const [providerLoading, setProviderLoading] = useState(true);
    const [providerStats, setProviderStats] = useState({
        total: 0,
        paid: 0,
        unpaid: 0,
        overdue: 0,
        totalAmount: 0,
        paidAmount: 0,
        amountsByCurrency: {}
    });

    // États pour les filtres
    const [filters, setFilters] = useState({
        dateFrom: '2024-01-01',
        dateTo: new Date().toISOString().split('T')[0],
        status: '',
    });

    // État pour le modal d'ajout
    const [showAddModal, setShowAddModal] = useState(false);

    const [activeSection, setActiveSection] = useState('customer'); // 'customer' ou 'provider'

    // URLs API
    const invoice_url = process.env.REACT_APP_API_INVOICE_URL;

    // Calculer les statistiques clients avec regroupement par devise
    const calculateCustomerStats = (invoices) => {
        const stats = {
            total: invoices.length,
            paid: 0,
            unpaid: 0,
            overdue: 0,
            totalAmount: 0,
            paidAmount: 0,
            amountsByCurrency: {}
        };

        invoices.forEach(invoice => {
            const amount = parseFloat(invoice.amount) || 0;
            const currency = invoice.currency || 'USD';
            stats.totalAmount += amount;

            // Regrouper par devise
            if (!stats.amountsByCurrency[currency]) {
                stats.amountsByCurrency[currency] = 0;
            }
            stats.amountsByCurrency[currency] += amount;

            switch (invoice.status?.toLowerCase()) {
                case 'paid':
                    stats.paid++;
                    stats.paidAmount += amount;
                    break;
                case 'unpaid':
                    stats.unpaid++;
                    break;
                case 'overdue':
                    stats.overdue++;
                    break;
            }
        });

        setCustomerStats(stats);
    };

    // Calculer les statistiques fournisseurs avec regroupement par devise
    const calculateProviderStats = (invoices) => {
        const stats = {
            total: invoices.length,
            paid: 0,
            unpaid: 0,
            overdue: 0,
            totalAmount: 0,
            paidAmount: 0,
            amountsByCurrency: {}
        };

        invoices.forEach(invoice => {
            const amount = parseFloat(invoice.amount) || 0;
            const currency = invoice.currency || 'USD';
            stats.totalAmount += amount;

            // Regrouper par devise
            if (!stats.amountsByCurrency[currency]) {
                stats.amountsByCurrency[currency] = 0;
            }
            stats.amountsByCurrency[currency] += amount;

            switch (invoice.status?.toLowerCase()) {
                case 'paid':
                    stats.paid++;
                    stats.paidAmount += amount;
                    break;
                case 'unpaid':
                    stats.unpaid++;
                    break;
                case 'overdue':
                    stats.overdue++;
                    break;
            }
        });

        setProviderStats(stats);
    };

    // Charger les factures clients
    const fetchCustomerInvoices = async () => {
        try {
            setCustomerLoading(true);
            const response = await api.post(`${invoice_url}/customer/list`, {
                customerId: '',
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                status: filters.status || undefined,
                invoiceNumber: filters.invoiceNumber || undefined
            });

            if (response.data && response.data.result) {
                const invoices = response.data.result.data || [];
                setCustomerInvoices(invoices);
                calculateCustomerStats(invoices);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des factures clients:', error);
            toast.error(t('errorLoadingCustomerInvoices'));
            setCustomerInvoices([]);
        } finally {
            setCustomerLoading(false);
        }
    };

    // Charger les factures fournisseurs
    const fetchProviderInvoices = async () => {
        try {
            setProviderLoading(true);
            const response = await api.post(`${invoice_url}/provider/list`, {
                providerId: '',
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                status: filters.status || undefined,
                invoiceNumber: filters.invoiceNumber || undefined
            });

            if (response.data && response.data.result) {
                const invoices = response.data.result.data || [];
                setProviderInvoices(invoices);
                calculateProviderStats(invoices);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des factures fournisseurs:', error);
            toast.error(t('errorLoadingProviderInvoices'));
            setProviderInvoices([]);
        } finally {
            setProviderLoading(false);
        }
    };

    // Appliquer les filtres
    const applyFilters = () => {
        fetchCustomerInvoices();
        fetchProviderInvoices();
    };

    // Réinitialiser les filtres
    const resetFilters = () => {
        setFilters({
            dateFrom: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
            dateTo: new Date().toISOString().split('T')[0],
            status: '',
            invoiceNumber: ''
        });
    };

    // Exporter les factures clients
    const exportToExcelCustomer = () => {
        const invoices = customerInvoices;
        if (!invoices || invoices.length === 0) {
            toast.error(t('noDataToExport'));
            return;
        }

        try {
            // Calculer les statistiques pour l'export
            const invoiceStats = {
                totalInvoices: invoices.length,
                paidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'paid').length,
                unpaidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'unpaid').length,
                overdueInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'overdue').length,
                amountsByCurrency: {}
            };

            invoices.forEach(invoice => {
                const currency = invoice.currency || 'USD';
                const amount = parseFloat(invoice.amount) || 0;

                if (!invoiceStats.amountsByCurrency[currency]) {
                    invoiceStats.amountsByCurrency[currency] = 0;
                }
                invoiceStats.amountsByCurrency[currency] += amount;
            });

            const currencyRows = Object.entries(invoiceStats.amountsByCurrency).map(([currency, amount]) => ({
                [t('statistics')]: `${t('total')} (${currency})`,
                '': amount.toFixed(2)
            }));

            // Préparer les données pour Excel (dans l'ordre des colonnes du tableau)
            const excelData = invoices.map(invoice => ({
                [t('id')]: invoice.id,
                [t('invoiceNumber')]: invoice.invoice_number,
                [t('date')]: invoice.invoice_at,
                [t('status')]: invoice.status,
                [t('amount')]: invoice.amount,
                [t('paid_at')]: invoice.paid_at || t('notAvailable'),
                [t('currency')]: invoice.currency,
                [t('customer')]: invoice.customer_name || 'N/A'
            }));

            // Créer une feuille de calcul
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Préparer les statistiques
            const statsData = [
                { [t('statistics')]: '' },
                { [t('statistics')]: t('totalInvoices'), '': invoiceStats.totalInvoices },
                { [t('statistics')]: t('paidInvoices'), '': invoiceStats.paidInvoices },
                { [t('statistics')]: t('unpaidInvoices'), '': invoiceStats.unpaidInvoices },
                { [t('statistics')]: t('overdueInvoices'), '': invoiceStats.overdueInvoices },
                { [t('statistics')]: '' },
                { [t('statistics')]: `--- ${t('totalByCurrency')} ---`, '': '' },
                ...currencyRows,
                { [t('statistics')]: '' },
                { [t('statistics')]: t('exportInformation'), '': '' },
                { [t('statistics')]: t('exportDate'), '': new Date().toLocaleString(lang) },
                { [t('statistics')]: t('filtersApplied'), '': '' },
                { [t('statistics')]: t('dateFrom'), '': filters.dateFrom || t('none') },
                { [t('statistics')]: t('dateTo'), '': filters.dateTo || t('none') },
                { [t('statistics')]: t('statusFilter'), '': filters.status ? t(filters.status) : t('allStatus') },
                { [t('statistics')]: t('invoiceNumberFilter'), '': filters.invoiceNumber || t('none') },
            ];

            const wsStats = XLSX.utils.json_to_sheet(statsData, { skipHeader: true });

            // Créer un nouveau classeur avec plusieurs feuilles
            const wb = XLSX.utils.book_new();

            // Ajouter la feuille des données des factures
            XLSX.utils.book_append_sheet(wb, ws, t('invoices'));

            // Ajouter la feuille des statistiques
            XLSX.utils.book_append_sheet(wb, wsStats, t('statistics'));

            // Mettre en forme les colonnes (largeur automatique)
            if (ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
                const wscols = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    let maxLength = 0;
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
                        if (cell && cell.v) {
                            const cellLength = cell.v.toString().length;
                            if (cellLength > maxLength) maxLength = cellLength;
                        }
                    }
                    wscols.push({ wch: Math.min(maxLength + 2, 40) }); // Max 40 caractères
                }
                ws['!cols'] = wscols;
            }

            // Mettre en forme les statistiques
            if (wsStats['!ref']) {
                const range = XLSX.utils.decode_range(wsStats['!ref']);
                const wscolsStats = [
                    { wch: 30 }, // Première colonne (labels)
                    { wch: 25 }  // Deuxième colonne (valeurs)
                ];
                wsStats['!cols'] = wscolsStats;
            }

            // Générer le nom du fichier avec la date et le nom du fournisseur
            const date = new Date();
            const dateStr = date.toISOString().split('T')[0];
            const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
            const fileName = `invoices_${t('customer')}_${dateStr}_${t('to')}_${timeStr}.xlsx`;

            // Télécharger le fichier
            XLSX.writeFile(wb, fileName);

            toast.success(t('exportSuccess'));

        } catch (error) {
            console.error('Erreur lors de l\'export Excel:', error);
            toast.error(t('exportError'));
        }
    };

    // Exporter les factures fournisseurs
    const exportToExcelProvider = () => {
        const invoices = providerInvoices;
        if (!invoices || invoices.length === 0) {
            toast.error(t('noDataToExport'));
            return;
        }

        try {
            // Calculer les statistiques pour l'export
            const invoiceStats = {
                totalInvoices: invoices.length,
                paidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'paid').length,
                unpaidInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'unpaid').length,
                overdueInvoices: invoices.filter(inv => inv.status.toLowerCase() === 'overdue').length,
                amountsByCurrency: {}
            };

            invoices.forEach(invoice => {
                const currency = invoice.currency || 'USD';
                const amount = parseFloat(invoice.amount) || 0;

                if (!invoiceStats.amountsByCurrency[currency]) {
                    invoiceStats.amountsByCurrency[currency] = 0;
                }
                invoiceStats.amountsByCurrency[currency] += amount;
            });

            const currencyRows = Object.entries(invoiceStats.amountsByCurrency).map(([currency, amount]) => ({
                [t('statistics')]: `${t('total')} (${currency})`,
                '': amount.toFixed(2)
            }));

            // Préparer les données pour Excel (dans l'ordre des colonnes du tableau)
            const excelData = invoices.map(invoice => ({
                [t('id')]: invoice.id,
                [t('invoiceNumber')]: invoice.invoice_number,
                [t('date')]: invoice.invoice_at,
                [t('status')]: invoice.status,
                [t('amount')]: invoice.amount,
                [t('paid_at')]: invoice.paid_at || t('notAvailable'),
                [t('currency')]: invoice.currency,
                [t('provider')]: invoice.provider_name || 'N/A'
            }));

            // Créer une feuille de calcul
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Préparer les statistiques
            const statsData = [
                { [t('statistics')]: '' },
                { [t('statistics')]: t('totalInvoices'), '': invoiceStats.totalInvoices },
                { [t('statistics')]: t('paidInvoices'), '': invoiceStats.paidInvoices },
                { [t('statistics')]: t('unpaidInvoices'), '': invoiceStats.unpaidInvoices },
                { [t('statistics')]: t('overdueInvoices'), '': invoiceStats.overdueInvoices },
                { [t('statistics')]: '' },
                { [t('statistics')]: `--- ${t('totalByCurrency')} ---`, '': '' },
                ...currencyRows,
                { [t('statistics')]: '' },
                { [t('statistics')]: t('exportInformation'), '': '' },
                { [t('statistics')]: t('exportDate'), '': new Date().toLocaleString(lang) },
                { [t('statistics')]: t('filtersApplied'), '': '' },
                { [t('statistics')]: t('dateFrom'), '': filters.dateFrom || t('none') },
                { [t('statistics')]: t('dateTo'), '': filters.dateTo || t('none') },
                { [t('statistics')]: t('statusFilter'), '': filters.status ? t(filters.status) : t('allStatus') },
                { [t('statistics')]: t('invoiceNumberFilter'), '': filters.invoiceNumber || t('none') },
            ];

            const wsStats = XLSX.utils.json_to_sheet(statsData, { skipHeader: true });

            // Créer un nouveau classeur avec plusieurs feuilles
            const wb = XLSX.utils.book_new();

            // Ajouter la feuille des données des factures
            XLSX.utils.book_append_sheet(wb, ws, t('invoices'));

            // Ajouter la feuille des statistiques
            XLSX.utils.book_append_sheet(wb, wsStats, t('statistics'));

            // Mettre en forme les colonnes (largeur automatique)
            if (ws['!ref']) {
                const range = XLSX.utils.decode_range(ws['!ref']);
                const wscols = [];
                for (let C = range.s.c; C <= range.e.c; ++C) {
                    let maxLength = 0;
                    for (let R = range.s.r; R <= range.e.r; ++R) {
                        const cell = ws[XLSX.utils.encode_cell({ c: C, r: R })];
                        if (cell && cell.v) {
                            const cellLength = cell.v.toString().length;
                            if (cellLength > maxLength) maxLength = cellLength;
                        }
                    }
                    wscols.push({ wch: Math.min(maxLength + 2, 40) }); // Max 40 caractères
                }
                ws['!cols'] = wscols;
            }

            // Mettre en forme les statistiques
            if (wsStats['!ref']) {
                const range = XLSX.utils.decode_range(wsStats['!ref']);
                const wscolsStats = [
                    { wch: 30 }, // Première colonne (labels)
                    { wch: 25 }  // Deuxième colonne (valeurs)
                ];
                wsStats['!cols'] = wscolsStats;
            }

            // Générer le nom du fichier avec la date et le nom du fournisseur
            const date = new Date();
            const dateStr = date.toISOString().split('T')[0];
            const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-');
            const fileName = `invoices_${t('supplier')}_${dateStr}_${t('to')}_${timeStr}.xlsx`;
            // Télécharger le fichier
            XLSX.writeFile(wb, fileName);

            toast.success(t('exportSuccess'));

        } catch (error) {
            console.error('Erreur lors de l\'export Excel:', error);
            toast.error(t('exportError'));
        }
    };


    // Initialisation
    useEffect(() => {
        fetchCustomerInvoices();
        fetchProviderInvoices();
    }, []);

    // Colonnes pour le tableau clients
    const customerColumns = [
        {
            label: t('invoiceNumber'),
            key: 'invoice_number',
            sortable: true
        },
        {
            label: t('customer'),
            key: 'customer_name',
            sortable: true
        },
        {
            label: t('date'),
            key: 'invoice_at',
            sortable: true,
            render: (value) => new Date(value).toLocaleDateString(lang)
        },
        {
            label: t('status'),
            key: 'status',
            sortable: true,
            render: (value) => (
                <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'white',
                    background: value?.toLowerCase() === 'paid' ? '#10b981' :
                        value?.toLowerCase() === 'unpaid' ? '#f59e0b' : '#ef4444',
                    textTransform: 'capitalize'
                }}>
                    {value}
                </span>
            )
        },
        {
            label: t('amount'),
            key: 'amount',
            sortable: true,
            render: (value, row) => (
                <span style={{ fontWeight: '600' }}>
                    {parseFloat(value).toFixed(2)} {row.currency}
                </span>
            )
        },
        {
            label: t('paid_at'),
            key: 'paid_at',
            sortable: true,
            render: (value) => value ? new Date(value).toLocaleDateString(lang) : '-'
        },
    ];

    // Colonnes pour le tableau fournisseurs
    const providerColumns = [
        {
            label: t('invoiceNumber'),
            key: 'invoice_number',
            sortable: true
        },
        {
            label: t('provider'),
            key: 'provider_name',
            sortable: true
        },
        {
            label: t('date'),
            key: 'invoice_at',
            sortable: true,
            render: (value) => new Date(value).toLocaleDateString(lang)
        },
        {
            label: t('status'),
            key: 'status',
            sortable: true,
            render: (value) => (
                <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: 'white',
                    background: value?.toLowerCase() === 'paid' ? '#10b981' :
                        value?.toLowerCase() === 'unpaid' ? '#f59e0b' : '#ef4444',
                    textTransform: 'capitalize'
                }}>
                    {value}
                </span>
            )
        },
        {
            label: t('amount'),
            key: 'amount',
            sortable: true,
            render: (value, row) => (
                <span style={{ fontWeight: '600' }}>
                    {parseFloat(value).toFixed(2)} {row.currency}
                </span>
            )
        },
        {
            label: t('paid_at'),
            key: 'paid_at',
            sortable: true,
            render: (value) => value ? new Date(value).toLocaleDateString(lang) : '-'
        },
    ];

    // Gérer le clic sur une ligne
    const handleRowClick = (invoice) => {
        const isCustomer = invoice.customer_name !== undefined;
        if (isCustomer) {
            navigate(`/invoices/customers/${invoice.id}`);
        } else {
            navigate(`/invoices/providers/${invoice.id}`);
        }
    };

    const handleDetails = (invoice) => {
        handleRowClick(invoice);
    };

    // Gérer l'ajout de facture
    const handleAddInvoiceSuccess = () => {
        fetchCustomerInvoices();
        toast.success(t('invoiceCreatedSuccess'));
    };

    // Styles
    const styles = {
        container: {
            padding: '24px',
            background: '#f9fafb',
            minHeight: '100vh',
            overflowX: 'hidden' // Empêche le débordement horizontal
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e5e7eb'
        },
        title: {
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        // Navigation entre sections
        sectionNav: {
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            background: 'white',
            padding: '12px',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        },
        navButton: {
            flex: 1,
            padding: '12px 16px',
            border: 'none',
            borderRadius: '8px',
            background: '#f3f4f6',
            color: '#6b7280',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            fontSize: '14px'
        },
        activeNavButton: {
            background: '#7c3aed',
            color: 'white'
        },
        filtersContainer: {
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        filtersTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
        },
        filtersGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
        },
        filterGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        filterLabel: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
        },
        filterInput: {
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            transition: 'all 0.2s ease',
            '&:focus': {
                outline: 'none',
                borderColor: '#7c3aed',
                boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
            }
        },
        filterSelect: {
            padding: '10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            background: 'white',
            transition: 'all 0.2s ease',
            '&:focus': {
                outline: 'none',
                borderColor: '#7c3aed',
                boxShadow: '0 0 0 3px rgba(124, 58, 237, 0.1)'
            }
        },
        filterButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        applyButton: {
            padding: '10px 20px',
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#6d28d9'
            }
        },
        resetButton: {
            padding: '10px 20px',
            background: 'transparent',
            color: '#4b5563',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#f3f4f6'
            }
        },
        section: {
            background: 'white',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '32px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        },
        sectionHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            paddingBottom: '16px',
            padding: '12px',
            borderBottom: '1px solid #e5e7eb'
        },
        sectionTitle: {
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
        },
        sectionButtons: {
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
        },
        exportButton: {
            padding: '10px 20px',
            background: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#0da271'
            }
        },
        addButton: {
            padding: '10px 20px',
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            '&:hover': {
                background: '#6d28d9'
            }
        },
        // Statistiques plus discrètes
        statsGrid: {
            padding: '12px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
        },
        statCard: {
            background: 'white',
            border: '1px solid #7c3aed',
            color: '#374151',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
        },
        statIcon: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            background: '#f3f4f6',
            borderRadius: '8px',
            marginBottom: '12px'
        },
        statValue: {
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '4px',
            color: '#1f2937'
        },
        statLabel: {
            fontSize: '13px',
            color: '#6b7280',
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        statSubtext: {
            fontSize: '12px',
            color: '#9ca3af',
            fontStyle: 'italic'
        },
        // Total amount par devise (discret)
        totalSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '24px',
            padding: '20px',
            background: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
        },
        totalTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        currenciesGrid: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px'
        },
        currencyBadge: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '20px',
            fontSize: '14px',
            color: '#374151'
        },
        currencyAmount: {
            fontWeight: '600',
            color: '#1f2937'
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #d1d5db'
        },
        loadingText: {
            color: '#6b7280',
            fontSize: '18px'
        },
        noData: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6b7280',
            background: '#f9fafb',
            borderRadius: '12px',
            border: '2px dashed #d1d5db'
        },
        // Boutons de navigation flottants pour mobile/tablette
        floatingNavButtons: {
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            zIndex: 1000
        },
        floatingNavButton: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#7c3aed',
            color: 'white',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
            transition: 'all 0.2s ease'
        }
    };

    // Rendu du total par devise
    const renderTotalByCurrency = (amountsByCurrency) => {
        const currencies = Object.keys(amountsByCurrency);
        if (currencies.length === 0) return null;

        return (
            <div style={styles.totalSection}>
                <h4 style={styles.totalTitle}>
                    <DollarSign size={18} />
                    {t('totalByCurrency')}
                </h4>
                <div style={styles.currenciesGrid}>
                    {currencies.map(currency => (
                        <div key={currency} style={styles.currencyBadge}>
                            <span style={styles.currencyAmount}>
                                {amountsByCurrency[currency].toFixed(2)}
                            </span>
                            <span>{currency}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <FileText size={32} color="#7c3aed" />
                    {t('invoices')}
                </h1>
            </div>

            {/* Navigation entre sections */}
            <div style={styles.sectionNav}>
                <button
                    style={{
                        ...styles.navButton,
                        ...(activeSection === 'customer' ? styles.activeNavButton : {})
                    }}
                    onClick={() => setActiveSection('customer')}
                >
                    <Users size={16} />
                    {t('customerInvoices')}
                </button>
                <button
                    style={{
                        ...styles.navButton,
                        ...(activeSection === 'provider' ? styles.activeNavButton : {})
                    }}
                    onClick={() => setActiveSection('provider')}
                >
                    <Building2 size={16} />
                    {t('providerInvoices')}
                </button>
            </div>

            {/* Filtres */}
            <div style={styles.filtersContainer}>
                <h3 style={styles.filtersTitle}>
                    <FileText size={20} />
                    {t('filters')}
                </h3>

                <div style={styles.filtersGrid}>
                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>
                            <Calendar size={16} />
                            {t('dateFrom')}
                        </label>
                        <input
                            type="date"
                            style={styles.filterInput}
                            value={filters.dateFrom}
                            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                        />
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>
                            <Calendar size={16} />
                            {t('dateTo')}
                        </label>
                        <input
                            type="date"
                            style={styles.filterInput}
                            value={filters.dateTo}
                            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                        />
                    </div>

                    <div style={styles.filterGroup}>
                        <label style={styles.filterLabel}>
                            <FileText size={16} />
                            {t('status')}
                        </label>
                        <select
                            style={styles.filterSelect}
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        >
                            <option value="">{t('allStatus')}</option>
                            <option value="paid">{t('paid')}</option>
                            <option value="unpaid">{t('unpaid')}</option>
                            <option value="overdue">{t('overdue')}</option>
                        </select>
                    </div>
                </div>

                <div style={styles.filterButtons}>
                    <button
                        style={styles.resetButton}
                        onClick={resetFilters}
                    >
                        {t('reset')}
                    </button>
                    <button
                        style={styles.applyButton}
                        onClick={applyFilters}
                    >
                        {t('applyFilters')}
                    </button>
                </div>
            </div>

            {/* Section Factures Clients */}
            {activeSection === 'customer' && (
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={styles.sectionTitle}>
                            <Users size={24} color="#7c3aed" />
                            {t('customerInvoices')}
                            <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
                                ({customerStats.total})
                            </span>
                        </h2>

                        <div style={styles.sectionButtons}>
                            <button
                                style={styles.exportButton}
                                onClick={exportToExcelCustomer}
                                disabled={customerInvoices.length === 0}
                            >
                                <Download size={16} />
                                {t('export')}
                            </button>
                            <button
                                style={styles.addButton}
                                onClick={() => setShowAddModal(true)}
                            >
                                <Plus size={16} />
                                {t('addInvoice')}
                            </button>
                        </div>
                    </div>

                    {/* Statistiques Clients */}
                    <div style={styles.statsGrid}>
                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <FileText size={20} color="#7c3aed" />
                            </div>
                            <div style={styles.statValue}>{customerStats.total}</div>
                            <div style={styles.statLabel}>{t('totalInvoices')}
                                <div style={styles.statSubtext}>
                                    {new Date(filters.dateFrom).toLocaleDateString(lang)} - {new Date(filters.dateTo).toLocaleDateString(lang)}
                                </div>
                            </div>

                        </div>

                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <DollarSign size={20} color="#10b981" />
                            </div>
                            <div style={styles.statValue}>{customerStats.paid}</div>
                            <div style={styles.statLabel}>{t('paidInvoices')}</div>
                        </div>

                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <TrendingUp size={20} color="#f59e0b" />
                            </div>
                            <div style={styles.statValue}>{customerStats.unpaid}</div>
                            <div style={styles.statLabel}>{t('unpaidInvoices')}</div>
                        </div>

                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <Calendar size={20} color="#ef4444" />
                            </div>
                            <div style={styles.statValue}>{customerStats.overdue}</div>
                            <div style={styles.statLabel}>{t('overdueInvoices')}</div>
                        </div>
                    </div>


                    {/* Tableau Clients */}
                    {customerLoading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.loadingText}>{t('loading')}...</div>
                        </div>
                    ) : customerInvoices.length > 0 ? (
                        <>

                            <ReusableTable
                                data={customerInvoices}
                                columns={customerColumns}
                                onRowClick={handleRowClick}
                                onDetails={handleDetails}
                                lang={lang}
                                itemsPerPageOptions={[10, 20, 50, 100]}
                                colors={{
                                    headerBg: '#7c3aed',
                                    buttonDetails: '#10b981'
                                }}
                                showEditButton={false}
                                showDeleteButton={false}
                            />

                            {/* Total par devise pour clients */}
                            {renderTotalByCurrency(customerStats.amountsByCurrency)}
                        </>
                    ) : (
                        <div style={styles.noData}>
                            <Users size={48} color="#9ca3af" />
                            <p style={{ marginTop: '16px', fontSize: '18px' }}>{t('noCustomerInvoices')}</p>
                            <p style={{ marginTop: '8px', color: '#6b7280' }}>
                                {t('useFiltersOrAddInvoice')}
                            </p>
                            <button
                                style={{
                                    ...styles.addButton,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#7c3aed'
                                }}
                                onClick={() => setShowAddModal(true)}
                            >
                                <Plus size={16} />
                                {t('createFirstInvoice')}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Section Factures Fournisseurs */}
            {activeSection === 'provider' && (
                <div style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={styles.sectionTitle}>
                            <Building2 size={24} color="#7c3aed" />
                            {t('providerInvoices')}

                            <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '8px' }}>
                                ({providerStats.total})
                            </span>
                        </h2>

                        <div style={styles.sectionButtons}>
                            <button
                                style={styles.exportButton}
                                onClick={exportToExcelProvider}
                                disabled={providerInvoices.length === 0}
                            >
                                <Download size={16} />
                                {t('export')}
                            </button>
                        </div>
                    </div>


                    {/* Statistiques Fournisseurs */}
                    <div style={styles.statsGrid}>
                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <FileText size={20} color="#7c3aed" />
                            </div>
                            <div style={styles.statValue}>{providerStats.total}</div>
                            <div style={styles.statLabel}>{t('totalInvoices')}
                                <div style={styles.statSubtext}>
                                    {new Date(filters.dateFrom).toLocaleDateString(lang)} - {new Date(filters.dateTo).toLocaleDateString(lang)}
                                </div>
                            </div>

                        </div>

                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <DollarSign size={20} color="#10b981" />
                            </div>
                            <div style={styles.statValue}>{providerStats.paid}</div>
                            <div style={styles.statSubtext}>{t('paidInvoices')}</div>
                        </div>

                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <TrendingUp size={20} color="#f59e0b" />
                            </div>
                            <div style={styles.statValue}>{providerStats.unpaid}</div>
                            <div style={styles.statLabel}>{t('unpaidInvoices')}</div>
                        </div>

                        <div style={styles.statCard}>
                            <div style={styles.statIcon}>
                                <Calendar size={20} color="#ef4444" />
                            </div>
                            <div style={styles.statValue}>{providerStats.overdue}</div>
                            <div style={styles.statLabel}>{t('overdueInvoices')}</div>
                        </div>
                    </div>

                    {/* Tableau Fournisseurs */}
                    {providerLoading ? (
                        <div style={styles.loadingContainer}>
                            <div style={styles.loadingText}>{t('loading')}...</div>
                        </div>
                    ) : providerInvoices.length > 0 ? (
                        <>
                            <div style={{ overflowX: 'hidden', maxWidth: '100%' }}>
                                <ReusableTable
                                    data={providerInvoices}
                                    columns={providerColumns}
                                    onRowClick={handleRowClick}
                                    onDetails={handleDetails}
                                    lang={lang}
                                    itemsPerPageOptions={[10, 20, 50, 100]}
                                    colors={{
                                        headerBg: '#7c3aed',
                                        buttonDetails: '#10b981'
                                    }}
                                    showEditButton={false}
                                    showDeleteButton={false}
                                />
                            </div>

                            {/* Total par devise pour fournisseurs */}
                            {renderTotalByCurrency(providerStats.amountsByCurrency)}
                        </>
                    ) : (
                        <div style={styles.noData}>
                            <Building2 size={48} color="#9ca3af" />
                            <p style={{ marginTop: '16px', fontSize: '18px' }}>{t('noProviderInvoices')}</p>
                            <p style={{ marginTop: '8px', color: '#6b7280' }}>
                                {t('useFiltersToFindInvoices')}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Boutons de navigation flottants */}
            <div style={styles.floatingNavButtons}>
                <button
                    style={styles.floatingNavButton}
                    onClick={() => setActiveSection(activeSection === 'customer' ? 'provider' : 'customer')}
                    title={activeSection === 'customer' ? t('switchToProviders') : t('switchToCustomers')}
                >
                    {activeSection === 'customer' ? <Building2 size={20} /> : <Users size={20} />}
                </button>
            </div>

            {/* Modal d'ajout de facture */}
            {showAddModal && (
                <AddInvoiceModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={handleAddInvoiceSuccess}
                    lang={lang}
                />
            )}

            <ToastContainer position="bottom-right" />
        </div>
    );
};

export default InvoicesPage;